import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Redirect, useParams } from "react-router-dom";

import { getPatientId } from "~/actions/get_patient_id";
import { ARCHES } from "~/common/constants";
import { isAdditionalAlignersCourse } from "~/common/courses";
import { getLastCorrection, isPatient } from "~/common/patient";
import { FormatDate } from "~/components/common/FormatDate";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { StatusBadge } from "~/components/ui/patient-status-badge";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { Table, TableCell, TableEmpty, TableHead, TableRow } from "~/components/ui/table";
import { useIntlDocumentTitle } from "~/hooks/use-document-title";
import { useScrollToTop } from "~/hooks/use-scroll-to-top";
import {
  clearExtraServices,
  getExtraServices,
  selectServices,
  selectServicesAreLoading,
} from "~/slices/services";
import { useAppDispatch, useAppSelector } from "~/store";
import type { NumericString } from "~/types/common";
import type { TService } from "~/types/service";

const ELASTICS_ID_TO_TRANSLATION = {
  2: "ER_R_II_CLASS",
  3: "ER_R_III_CLASS",
} as const;

export default function AdditionalAlignersTablePage() {
  return (
    <Layout>
      <RemoteLogErrorBoundary component="additional_aligners_table">
        <AdditionalAlignersTable />
      </RemoteLogErrorBoundary>
    </Layout>
  );
}

function AdditionalAlignersTable() {
  const { patient_id } = useParams<{ patient_id: NumericString }>();

  useIntlDocumentTitle("pat.additional.aligners.page.header", patient_id);
  useScrollToTop();

  const dispatch = useAppDispatch();

  const patient = useAppSelector((state) => state.patient);
  const services = useAppSelector(selectServices);
  const servicesAreLoading = useAppSelector(selectServicesAreLoading);
  const additionalAlignersServices = services.filter(
    (service) => service.tag == "ADDITIONAL_ALIGNERS",
  );

  useEffect(() => {
    dispatch(clearExtraServices());

    const patientIdAsNumber = Number(patient_id);
    void dispatch(getPatientId(patientIdAsNumber));
    void dispatch(getExtraServices(patientIdAsNumber));
  }, [patient_id, dispatch]);

  if (!isPatient(patient) || servicesAreLoading) {
    return <Loader />;
  }

  const { can_order_additional_aligners } = getLastCorrection(patient).order_options;

  if (!can_order_additional_aligners && !isAdditionalAlignersCourse(patient.course.course_id)) {
    return <Redirect to="/pages/patients" />;
  }

  return (
    <Portlet as="main" id="additional-aligners-table">
      <PortletTitle iconClassName="icon-users" id="patients-table-block-title">
        <FormattedMessage id="pat.additional.aligners.page.header" />
      </PortletTitle>

      <div className="portlet-body">
        <div id="additional-aligners_table_wrapper" className="dataTables_wrapper no-footer">
          <Table>
            <thead>
              <TableRow>
                <TableHead>
                  <FormattedMessage id="th.stage" />
                </TableHead>

                <TableHead>
                  <FormattedMessage id="th.option" />
                </TableHead>

                <TableHead>
                  <FormattedMessage id="AMOUNT" />
                </TableHead>

                <TableHead>
                  <FormattedMessage id="HEADER_ELASTICS" />
                </TableHead>

                <TableHead>
                  <FormattedMessage id="th.status" />
                </TableHead>

                <TableHead>
                  <FormattedMessage id="pat_table.received" />
                </TableHead>

                <TableHead>
                  <FormattedMessage id="th.timestamp" />
                </TableHead>
              </TableRow>
            </thead>

            <tbody>
              {additionalAlignersServices.length == 0 ? (
                <TableEmpty columns={7}>
                  <FormattedMessage id="additional_aligners.not_found" />
                </TableEmpty>
              ) : (
                additionalAlignersServices.map((service, index) => (
                  <TableRow key={index}>
                    <TableCell>{service.option.stage}</TableCell>

                    <TableCell>
                      {/* `treat_arch_id` can't be null for additional aligners */}
                      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                      <FormattedMessage id={ARCHES[service.option.treat_arch_id!]} />
                    </TableCell>

                    <TableCell>{service.option.amount}</TableCell>

                    <TableCell>{renderElastics(service)}</TableCell>

                    <TableCell>
                      <StatusBadge color="green">
                        <FormattedMessage id={service.option.status} />
                      </StatusBadge>
                    </TableCell>

                    <TableCell>
                      <FormattedMessage id={service.paid ? "bool.true" : "bool.false"} />
                    </TableCell>

                    <TableCell>
                      <FormatDate value={service.timestamp} date />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </Portlet>
  );
}

function renderElastics(service: TService) {
  const { elastics_left, elastics_right } = service.option;
  if (elastics_left !== null && elastics_right !== null) {
    return (
      <>
        <FormattedMessage id="ELASTICS_RIGHT" />
        &nbsp;
        <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_right]} />
        <br />
        <FormattedMessage id="ELASTICS_LEFT" />
        &nbsp;
        <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_left]} />
      </>
    );
  } else if (elastics_left !== null) {
    return (
      <>
        <FormattedMessage id="ELASTICS_LEFT" />
        &nbsp;
        <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_left]} />
      </>
    );
  } else if (elastics_right !== null) {
    return (
      <>
        <FormattedMessage id="ELASTICS_RIGHT" />
        &nbsp;
        <FormattedMessage id={ELASTICS_ID_TO_TRANSLATION[elastics_right]} />
      </>
    );
  }
}
